import { useCallback, useEffect, useMemo, useState } from "react";
import map from "ramda/src/map";
import uniq from "ramda/src/uniq";
import concat from "ramda/src/concat";
import curry from "ramda/src/curry";
import compose from "ramda/src/compose";

import useAuth from "../../routes/login/AuthContext";
import { GetAllNetworkInfo, L3, L4, Network } from "../../services/NetworkApi";
import { normalize } from "../../helpers/normalize";
import { useNetworksContext } from "../NetworksProvider";
import { normalizeNetworks } from "../../helpers/normalizeNetworks";
import { propL1IdEq } from "../../helpers/propL1IdEq";
import { updateDataLayerOnNetworkChange } from "../../helpers/analytics";

const handleNetworkUpdate = curry(
  (l1Id: number, data: Partial<Network>, network: Network) => {
    if (network.l1Id === l1Id) {
      return {
        ...network,
        ...data,
      };
    }

    return network;
  }
);

const handleNetworkGenderSettingsUpdate = curry(
  (
    l1Id: number,
    l2Id: number,
    data: Partial<Network["genderNetworkSettingsTransport"]>,
    network: Network
  ) => {
    if (network.l1Id === l1Id && network.l2Id === l2Id) {
      return {
        ...network,
        genderNetworkSettingsTransport: {
          ...network.genderNetworkSettingsTransport,
          ...data,
        },
      };
    }

    return network;
  }
);

const handleSubNetworkUpdate = curry(
  (l1Id: number, l2Id: number, data: Partial<Network>, network: Network) => {
    if (network.l1Id === l1Id && network.l2Id === l2Id) {
      return {
        ...network,
        ...data,
      };
    }

    return network;
  }
);

const handleUnitTypeUpdate = curry(
  (
    l1Id: number,
    l2Id: number,
    l3Id: number,
    data: Partial<L3>,
    network: Network
  ) => {
    if (network.l1Id === l1Id && network.l2Id === l2Id) {
      return {
        ...network,
        l3: network.l3.map((l3) => {
          if (l3.l3Id === l3Id) {
            return {
              ...l3,
              ...data,
            };
          }

          return l3;
        }),
      };
    }

    return network;
  }
);

const handleRoomUpdate = curry(
  (
    l1Id: number,
    l2Id: number,
    l3Id: number,
    l4Id: number,
    data: Partial<L4>,
    network: Network
  ) => {
    if (network.l1Id === l1Id && network.l2Id === l2Id) {
      return {
        ...network,
        l3: network.l3.map((l3) => {
          if (l3.l3Id === l3Id) {
            return {
              ...l3,
              l4: l3.l4?.map((l4) => {
                if (l4.l4Id === l4Id) {
                  return {
                    ...l4,
                    ...data,
                  };
                }

                return l4;
              }),
            };
          }

          return l3;
        }),
      };
    }

    return network;
  }
);

export const useContextValue = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedNetworks, setSelectedNetworks] = useState<Network[]>([]);
  const { fetchNetworks, networks, setNetworks } = useNetworksContext();
  const [selectedNetworkId, setSelectedNetworkId] = useState(0);
  const { getAuthToken, isSuperAdmin, isLoggedIn } = useAuth();

  const normalizedNetworks = normalizeNetworks(networks);

  useEffect(() => {
    if (isSuperAdmin() || !isLoggedIn()) {
      return;
    }

    fetchNetworks().then((networks) => {
      if(networks) {
        const uniqueNetworks = new Set(networks.map(n => n.l1Id));
        uniqueNetworks.size === 1 && setSelectedNetworkId(uniqueNetworks.values().next().value);
      }
});
  }, [isLoggedIn()]);

  useEffect(() => {
    if (selectedNetworkId === 0) return;

    const selectedNetworks = networks.filter(propL1IdEq(selectedNetworkId));
    setSelectedNetworks(selectedNetworks);
  }, [normalizedNetworks, selectedNetworkId]);

  const normalizedSelectedNetworks = normalize(selectedNetworks);
  const reloadNetworks = useCallback(() => fetchNetworks(), [fetchNetworks]);

  const shallowReloadNetworks = useCallback(async () => fetchNetworks(true), [
    fetchNetworks,
  ]);

  const loadNetworkInfoForNetworks = useCallback(
    async (networkIds: number[], withLoading = false) => {
      if (withLoading) {
        setIsLoading(true);
      }

      // const networksToLoad = networkIds
      //   .filter((id) => id !== 0)
      //   .filter(
      //     (id) => !selectedNetworks.find((network) => network.l1Id === id)
      //   );

      const res = await GetAllNetworkInfo(networkIds, await getAuthToken());

      if (withLoading) {
        setIsLoading(false);
      }

      if (!res.networks) {
        return;
      }

      if (isSuperAdmin()) {
        setNetworks(res.networks);
      }

      setSelectedNetworks(
        // @ts-ignore
        compose<Network[], Network[], Network[]>(uniq, concat(res.networks))
      );

      return res;
    },
    [normalizedSelectedNetworks, isSuperAdmin()]
  );

  const updateNetworkValues = useCallback(
    (l1Id: number, data: Partial<Network>) =>
      setSelectedNetworks(map(handleNetworkUpdate(l1Id, data))),
    [normalizedSelectedNetworks]
  );

  const updateGenderNetworkSettingsValues = useCallback(
    (
      l1Id: number,
      l2Id: number,
      data: Partial<Network["genderNetworkSettingsTransport"]>
    ) =>
      setSelectedNetworks(
        map(handleNetworkGenderSettingsUpdate(l1Id, l2Id, data))
      ),
    [normalizedSelectedNetworks]
  );

  const updateSubNetworkValues = useCallback(
    (l1Id: number, l2Id: number, data: Partial<Network>) =>
      setSelectedNetworks(map(handleSubNetworkUpdate(l1Id, l2Id, data))),
    [normalizedSelectedNetworks]
  );

  const updateUnitTypeValues = useCallback(
    (l1Id: number, l2Id: number, l3Id: number, data: Partial<L3>) =>
      setSelectedNetworks(map(handleUnitTypeUpdate(l1Id, l2Id, l3Id, data))),
    [normalizedSelectedNetworks]
  );

  const updateRoomValues = useCallback(
    (
      l1Id: number,
      l2Id: number,
      l3Id: number,
      l4Id: number,
      data: Partial<L4>
    ) =>
      setSelectedNetworks(map(handleRoomUpdate(l1Id, l2Id, l3Id, l4Id, data))),
    [normalizedSelectedNetworks]
  );

  const selectedNetworkValue = useMemo(
    () => ({
      portfolioType: selectedNetworks.find(
        ({ l1Id }) => l1Id === selectedNetworkId
      )?.portfolio,
      selectedNetworkId,
      setSelectedNetworkId,
    }),
    [selectedNetworkId, selectedNetworks]
  );

  // Update Google Tag Manager dataLayer when selected network changes.
  useEffect(() => {
    const selectedNetwork = selectedNetworks.find(
      ({ l1Id }) => l1Id === selectedNetworkId
    );

    if (selectedNetwork && selectedNetworkId !== 0) {
      updateDataLayerOnNetworkChange(selectedNetwork, isSuperAdmin());
    }
  }, [selectedNetworkId, selectedNetworks, isSuperAdmin]);

  const networksValue = useMemo(
    () => ({
      networks: selectedNetworks,
      isLoading,
      reloadNetworks,
      updateSubNetworkValues,
      shallowReloadNetworks,
      updateUnitTypeValues,
      updateRoomValues,
      updateNetworkValues,
      updateGenderNetworkSettingsValues,
      loadNetworkInfoForNetworks,
    }),
    [
      selectedNetworks,
      loadNetworkInfoForNetworks,
      normalizedSelectedNetworks,
      isLoading,
      reloadNetworks,
      updateSubNetworkValues,
      updateNetworkValues,
      updateRoomValues,
      shallowReloadNetworks,
      updateUnitTypeValues,
      updateGenderNetworkSettingsValues,
    ]
  );

  return {
    networksValue,
    selectedNetworkValue,
  };
};
